import axiosInstance from '../../../core/services/axiosConfig';
import { apis } from '../../../core/services/params';
import React, { useState, useEffect } from "react";
import { Modal, Typography, Card, Tag, Spin, Row, Col, Statistic, Descriptions, List, Badge, Divider, Tooltip, message } from "antd";
import {
    UserOutlined,
    RobotOutlined,
    ThunderboltOutlined,
    WarningOutlined,
    ClockCircleOutlined,
    PhoneOutlined,
    CloseCircleOutlined,
    SolutionOutlined,
    FileTextOutlined,
    CalendarOutlined,
    CheckCircleOutlined,
    CustomerServiceOutlined,
    CopyOutlined,
    DownloadOutlined,
} from "@ant-design/icons";
import moment from "moment";

const { Text, Paragraph } = Typography;

// @@ AI-COMMENT: Props for the Voice AI call detail modal
interface SaharaCallDetailsProps {
    open: boolean;
    ucid: string;
    onClose: () => void;
}

// @@ AI-COMMENT: Voice AI call detail modal — mirrors OzonetelCallDetails layout with 3-column design
const SaharaCallDetails: React.FC<SaharaCallDetailsProps> = ({ open, ucid, onClose }) => {
    const [data, setData] = useState<any>(null);
    const [loading, setLoading] = useState(false);

    // @@ AI-COMMENT: Toggle between simple chat and full detailed view modes
    const [viewMode, setViewMode] = useState<'simple' | 'detailed'>('simple');

    // @@ AI-COMMENT: Build a plain-text transcript from events or fallback transcript array
    const buildTranscriptText = (): string => {
        if (!data) return '';
        const lines: string[] = [
            `Voice AI Transcript — UCID: ${data.ucid}`,
            `Call Time: ${moment(data.start_time).format('YYYY-MM-DD HH:mm:ss')} → ${moment(data.end_time).format('HH:mm:ss')}`,
            '─'.repeat(60),
        ];
        if (data.events && data.events.length > 0) {
            // @@ AI-COMMENT: Use events array — only USER_TURN and AGENT_TURN contribute to transcript
            data.events.forEach((event: any) => {
                if (!['USER_TURN', 'AGENT_TURN'].includes(event.type)) return;
                const speaker = event.type === 'USER_TURN' ? 'User' : 'Sahara';
                const time = moment(event.timestamp).format('HH:mm:ss');
                lines.push(`[${time}] ${speaker}: ${event.details?.text || ''}`);
            });
        } else if (data.analysis?.transcript?.length > 0) {
            // @@ AI-COMMENT: Fallback to analysis.transcript array
            data.analysis.transcript.forEach((entry: any) => {
                const speaker = entry.speaker === 'speaker1' ? 'Sahara' : 'User';
                lines.push(`${speaker}: ${entry.text || ''}`);
            });
        }
        return lines.join('\n');
    };

    // @@ AI-COMMENT: Copy transcript text to clipboard using Clipboard API
    const handleCopyTranscript = async () => {
        const text = buildTranscriptText();
        if (!text) { message.warning('No transcript available'); return; }
        await navigator.clipboard.writeText(text);
        message.success('Transcript copied to clipboard');
    };

    // @@ AI-COMMENT: Download transcript as a .txt file
    const handleDownloadTranscript = () => {
        const text = buildTranscriptText();
        if (!text) { message.warning('No transcript available'); return; }
        const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `transcript_${data.ucid}_${moment(data.start_time).format('YYYYMMDD_HHmmss')}.txt`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const fetchDetails = async () => {
        if (!ucid) return;
        setLoading(true);
        try {
            const response = await axiosInstance.get(`${apis.OZONETEL_VOICE_AI_AGENT_DETAILS}?ucid=${ucid}`);
            const result = response.data;
            if (result.status) {
                setData(result.data);
            }
        } catch (error) {
            console.error("Error fetching call details:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (open && ucid) {
            fetchDetails();
        } else {
            setData(null);
            setViewMode('simple');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, ucid]);

    // @@ AI-COMMENT: Sentiment score bar — same style as OzonetelCallDetails
    const renderSentimentBar = (score: number) => (
        <div className="flex items-center gap-2 w-full">
            <div className="flex-1 bg-gray-200 rounded-full h-2">
                <div
                    className={`h-2 rounded-full ${score >= 4 ? 'bg-green-500' : score >= 3 ? 'bg-blue-500' : 'bg-red-500'}`}
                    style={{ width: `${(score / 5) * 100}%` }}
                />
            </div>
            <span className="text-xs font-semibold w-6 text-right">{score}/5</span>
        </div>
    );

    // @@ AI-COMMENT: Render a single conversation event based on its type
    const renderEvent = (event: any) => {
        const { type, details, timestamp } = event;
        const time = moment(timestamp).format("HH:mm:ss");

        if (viewMode === 'simple' && !['USER_TURN', 'AGENT_TURN'].includes(type)) {
            return null;
        }

        switch (type) {
            case "USER_TURN":
                return (
                    <div className="flex flex-col items-end mb-4">
                        <div className="flex items-center gap-2 mb-1">
                            <span className="text-xs text-gray-400">{time}</span>
                            <span className="font-semibold text-gray-600">User</span>
                            <UserOutlined className="text-blue-500" />
                        </div>
                        <div className="bg-blue-50 border border-blue-100 rounded-lg rounded-tr-none p-3 max-w-[80%] shadow-sm">
                            <Text className="text-gray-800">{details.text}</Text>
                        </div>
                    </div>
                );
            case "AGENT_TURN":
                return (
                    <div className="flex flex-col items-start mb-4">
                        <div className="flex items-center gap-2 mb-1">
                            <RobotOutlined className="text-purple-500" />
                            <span className="font-semibold text-gray-600">Sahara</span>
                            <span className="text-xs text-gray-400">{time}</span>
                        </div>
                        <div className="bg-gray-50 border border-gray-100 rounded-lg rounded-tl-none p-3 max-w-[80%] shadow-sm">
                            <Text className="text-gray-800">{details.text}</Text>
                        </div>
                    </div>
                );
            case "TOOL_CALL":
                return (
                    <div className="flex justify-center mb-4 w-full">
                        <div className="border border-purple-100 bg-purple-50 rounded-lg p-3 w-[80%] shadow-sm flex flex-col items-center">
                            <div className="flex items-center gap-2 text-purple-700 font-medium mb-2">
                                <ThunderboltOutlined />
                                <span>Tool Call: {details.name}</span>
                                <span className="text-xs text-gray-400 font-normal">({time})</span>
                            </div>
                            <div className="w-full bg-white rounded p-2 border border-purple-100 font-mono text-xs overflow-x-auto text-gray-600">
                                {JSON.stringify(details.args, null, 2)}
                            </div>
                        </div>
                    </div>
                );
            case "INTERRUPTION":
                return (
                    <div className="flex justify-center mb-4">
                        <Tag color="red" icon={<WarningOutlined />}>
                            Interruption at {time}
                        </Tag>
                    </div>
                );
            default:
                return null;
        }
    };

    // @@ AI-COMMENT: Shorthand references for analysis sub-sections
    const analysis = data?.analysis;
    const sentiment = analysis?.sentiment_and_profiling;

    return (
        <Modal
            open={open}
            onCancel={onClose}
            width="98%"
            style={{ top: 20 }}
            footer={null}
            title={
                // @@ AI-COMMENT: Modal header with UCID tag on right side
                <div className="flex justify-between items-center pr-8">
                    <div className="flex items-center gap-2 text-lg">
                        <RobotOutlined className="text-purple-600" />
                        Voice AI Analysis
                        {data && <Tag color="purple" className="ml-2 text-base">{data.ucid}</Tag>}
                    </div>
                </div>
            }
            bodyStyle={{ padding: '16px', height: 'calc(100vh - 100px)', overflowY: 'auto', backgroundColor: '#f5f7fa' }}
            maskClosable={false}
        >
            {loading ? (
                <div className="flex justify-center items-center h-full">
                    <Spin size="large" tip="Loading Voice AI analysis..." />
                </div>
            ) : data ? (
                <div className="space-y-4">
                    {/* @@ AI-COMMENT: Top stats row — mirrors OzonetelCallDetails header card */}
                    <Card size="small" className="shadow-sm border-0">
                        <Row gutter={24} align="middle">
                            <Col span={3}>
                                <Statistic
                                    title="Duration"
                                    value={moment.utc((data.metrics?.total_duration_sec || 0) * 1000).format("HH:mm:ss")}
                                    prefix={<ClockCircleOutlined />}
                                    valueStyle={{ fontSize: '1.25rem' }}
                                />
                            </Col>
                            <Col span={3}>
                                <Statistic
                                    title="Priority"
                                    value={analysis?.priority || '—'}
                                    valueStyle={{ color: analysis?.priority === 'High' ? '#cf1322' : '#389e0d', fontSize: '1.25rem' }}
                                />
                            </Col>
                            <Col span={4}>
                                <Statistic
                                    title="Issue Resolved"
                                    value={analysis?.call_metadata?.issue_resolved || '—'}
                                    prefix={analysis?.call_metadata?.issue_resolved === 'Yes'
                                        ? <CheckCircleOutlined className="text-green-500" />
                                        : <WarningOutlined className="text-orange-500" />}
                                    valueStyle={{ fontSize: '1.25rem' }}
                                />
                            </Col>
                            <Col span={4}>
                                <Statistic
                                    title="Customer"
                                    value={data.caller_phone}
                                    prefix={<PhoneOutlined />}
                                    valueStyle={{ fontSize: '1.25rem' }}
                                    formatter={(val) => (
                                        <a href={`tel:${val}`} className="text-gray-900 hover:text-blue-600 hover:underline">
                                            {String(val)}
                                        </a>
                                    )}
                                />
                            </Col>
                            <Col span={3}>
                                <Statistic
                                    title="Turns"
                                    value={data.metrics?.turn_count || 0}
                                    prefix={<RobotOutlined />}
                                    valueStyle={{ fontSize: '1.25rem' }}
                                />
                            </Col>
                            <Col span={7}>
                                <div className="flex flex-col h-full justify-center">
                                    <span className="text-gray-500 text-xs uppercase mb-1">Call Time</span>
                                    <div className="text-sm font-medium">{moment(data.start_time).format("MMM DD, YYYY HH:mm:ss")}</div>
                                    <div className="text-xs text-gray-400">End: {moment(data.end_time).format("HH:mm:ss")}</div>
                                </div>
                            </Col>
                        </Row>
                    </Card>

                    <Row gutter={16}>
                        {/* @@ AI-COMMENT: Left column — AI analysis panels (same structure as OzonetelCallDetails) */}
                        <Col span={7}>
                            <div className="space-y-4">
                                <Card title={<><SolutionOutlined /> Executive Summary</>} size="small" className="shadow-sm border-gray-200">
                                    <Paragraph className="text-gray-700 text-sm mb-0 text-justify">
                                        {analysis?.executive_summary || 'No summary available.'}
                                    </Paragraph>
                                </Card>

                                <Card title={<><FileTextOutlined /> Call Context &amp; Entities</>} size="small" className="shadow-sm border-gray-200">
                                    <Descriptions column={1} size="small" bordered className="text-xs">
                                        <Descriptions.Item label="Call Purpose">
                                            <span className="whitespace-pre-wrap">{analysis?.call_metadata?.call_purpose}</span>
                                        </Descriptions.Item>
                                        <Descriptions.Item label="Issue Category">
                                            <Tag color="cyan">{analysis?.entities?.issue_category}</Tag>
                                        </Descriptions.Item>
                                        <Descriptions.Item label="Suggested Issue">
                                            <Tag color="geekblue">{analysis?.entities?.suggested_issue_category}</Tag>
                                        </Descriptions.Item>
                                        <Descriptions.Item label="Item ID">
                                            {analysis?.entities?.item_id || <span className="text-gray-400">N/A</span>}
                                        </Descriptions.Item>
                                        <Descriptions.Item label="Order ID">
                                            {analysis?.entities?.order_id || <span className="text-gray-400">N/A</span>}
                                        </Descriptions.Item>
                                    </Descriptions>
                                </Card>

                                <Card title={<><CalendarOutlined /> Action Items &amp; Next Steps</>} size="small" className="shadow-sm border-gray-200">
                                    <List
                                        size="small"
                                        split={false}
                                        dataSource={analysis?.action_items || []}
                                        renderItem={(item: any) => (
                                            <List.Item className="px-0 py-1">
                                                <div className="flex items-start gap-2">
                                                    <Badge status="processing" className="mt-1.5" />
                                                    <span className="text-sm text-gray-700 leading-snug">{item}</span>
                                                </div>
                                            </List.Item>
                                        )}
                                    />
                                    <Divider className="my-3" />
                                    <div className="bg-blue-50 p-3 rounded border border-blue-100">
                                        <div className="text-xs font-bold text-blue-800 uppercase mb-1">Next Best Action</div>
                                        <div className="text-sm text-blue-900 leading-snug">
                                            {analysis?.next_best_action}
                                        </div>
                                    </div>
                                </Card>

                                {/* @@ AI-COMMENT: Sentiment & profiling — rendered only when analysis data exists */}
                                {sentiment && (
                                    <Card title={<><UserOutlined /> Sentiment &amp; Profiling</>} size="small" className="shadow-sm border-gray-200">
                                        <div className="space-y-4">
                                            <div>
                                                <div className="flex justify-between text-xs mb-1">
                                                    <span className="text-gray-500">Agent Professionalism</span>
                                                </div>
                                                {renderSentimentBar(sentiment.agent_professionalism_score)}
                                            </div>
                                            <div className="grid grid-cols-2 gap-4">
                                                <div>
                                                    <span className="text-xs text-gray-500 block">Agent Tone</span>
                                                    <Tag color="default">{sentiment.agent_tone}</Tag>
                                                </div>
                                                <div>
                                                    <span className="text-xs text-gray-500 block">Customer Tone</span>
                                                    <Tag color={sentiment.customer_tone === 'Angry' || sentiment.customer_tone === 'Anxious' ? 'red' : 'blue'}>
                                                        {sentiment.customer_tone}
                                                    </Tag>
                                                </div>
                                                <div className="col-span-2">
                                                    <span className="text-xs text-gray-500 block">Customer Satisfaction</span>
                                                    <span className="font-semibold text-gray-800">{sentiment.customer_satisfaction}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </Card>
                                )}
                            </div>
                        </Col>

                        {/* @@ AI-COMMENT: Middle column — technical / metrics details */}
                        <Col span={7}>
                            <div className="space-y-4">
                                <Card title={<><CustomerServiceOutlined /> Call Metrics</>} size="small" className="shadow-sm border-gray-200">
                                    <Descriptions column={1} size="small" bordered className="text-xs">
                                        <Descriptions.Item label="UCID">{data.ucid}</Descriptions.Item>
                                        <Descriptions.Item label="Total Duration">{data.total_call_duration}s</Descriptions.Item>
                                        <Descriptions.Item label="Tool Calls">{data.metrics?.tool_use_count}</Descriptions.Item>
                                        <Descriptions.Item label="Interruptions">{data.metrics?.interruption_count}</Descriptions.Item>
                                        <Descriptions.Item label="Total Tokens">
                                            {((data.metrics?.token_usage?.total_tokens || 0) / 1000).toFixed(1)}k
                                        </Descriptions.Item>
                                        <Descriptions.Item label="Input Tokens">
                                            {((data.metrics?.token_usage?.input_tokens || 0) / 1000).toFixed(1)}k
                                        </Descriptions.Item>
                                        <Descriptions.Item label="Output Tokens">
                                            {data.metrics?.token_usage?.output_tokens}
                                        </Descriptions.Item>
                                        <Descriptions.Item label="Start Time">
                                            {moment(data.start_time).format("YYYY-MM-DD HH:mm:ss")}
                                        </Descriptions.Item>
                                        <Descriptions.Item label="End Time">
                                            {data.end_time ? moment(data.end_time).format("YYYY-MM-DD HH:mm:ss") : '—'}
                                        </Descriptions.Item>
                                        <Descriptions.Item label="Telco Code">{data.telco_code}</Descriptions.Item>
                                    </Descriptions>
                                </Card>

                                {/* @@ AI-COMMENT: Hangup info if present */}
                                {data.hangup_data && (
                                    <Card title="Hangup Data" size="small" className="shadow-sm border-gray-200">
                                        <Descriptions column={1} size="small" bordered className="text-xs">
                                            <Descriptions.Item label="Event">{data.hangup_data.event}</Descriptions.Item>
                                            <Descriptions.Item label="Called Number">{data.hangup_data.called_number}</Descriptions.Item>
                                            <Descriptions.Item label="Caller ID">{data.hangup_data.cid}</Descriptions.Item>
                                            <Descriptions.Item label="Request Time">{data.hangup_data.request_time}</Descriptions.Item>
                                        </Descriptions>
                                    </Card>
                                )}

                                {/* @@ AI-COMMENT: Audio recording player if URL is available */}
                                {data.recording_url && (
                                    <Card size="small" className="shadow-sm border-gray-200 bg-gray-50">
                                        <div className="flex items-center gap-3">
                                            <CustomerServiceOutlined className="text-lg text-purple-600" />
                                            <div className="flex-1">
                                                <div className="text-xs font-semibold uppercase text-gray-500 mb-1">Call Recording</div>
                                                <audio controls src={data.recording_url} className="w-full h-8">
                                                    Your browser does not support the audio element.
                                                </audio>
                                            </div>
                                        </div>
                                    </Card>
                                )}
                            </div>
                        </Col>

                        {/* @@ AI-COMMENT: Right column — conversation timeline with Simple/Detailed toggle */}
                        <Col span={10}>
                            <Card
                                title="Conversation Timeline"
                                extra={
                                    <div className="flex items-center gap-2">
                                        {/* @@ AI-COMMENT: Copy and download transcript buttons */}
                                        <Tooltip title="Copy Transcript">
                                            <button
                                                onClick={handleCopyTranscript}
                                                className="flex items-center gap-1 px-2 py-1 text-xs rounded border border-gray-200 bg-white hover:bg-gray-50 text-gray-500 hover:text-blue-600 transition-colors"
                                            >
                                                <CopyOutlined />
                                                <span>Copy</span>
                                            </button>
                                        </Tooltip>
                                        <Tooltip title="Download as .txt">
                                            <button
                                                onClick={handleDownloadTranscript}
                                                className="flex items-center gap-1 px-2 py-1 text-xs rounded border border-gray-200 bg-white hover:bg-gray-50 text-gray-500 hover:text-green-600 transition-colors"
                                            >
                                                <DownloadOutlined />
                                                <span>.txt</span>
                                            </button>
                                        </Tooltip>
                                        {/* @@ AI-COMMENT: Simple Chat / Detailed View toggle pills */}
                                        <div className="flex bg-gray-100 p-1 rounded-lg">
                                            <button
                                                className={`px-3 py-1 text-xs rounded-md transition-all ${viewMode === 'simple' ? 'bg-white shadow text-purple-600 font-medium' : 'text-gray-500'}`}
                                                onClick={() => setViewMode('simple')}
                                            >
                                                Simple Chat
                                            </button>
                                            <button
                                                className={`px-3 py-1 text-xs rounded-md transition-all ${viewMode === 'detailed' ? 'bg-white shadow text-purple-600 font-medium' : 'text-gray-500'}`}
                                                onClick={() => setViewMode('detailed')}
                                            >
                                                Detailed View
                                            </button>
                                        </div>
                                    </div>
                                }
                                className="shadow-sm"
                                bodyStyle={{ padding: '16px', maxHeight: 'calc(100vh - 280px)', overflowY: 'auto' }}
                            >
                                {/* @@ AI-COMMENT: Render conversation events from events array if present, else from analysis transcript */}
                                {data.events && data.events.length > 0 ? (
                                    <>
                                        {data.events.map((event: any, index: number) => (
                                            <div key={index}>
                                                {renderEvent(event)}
                                            </div>
                                        ))}
                                        <div className="flex justify-center mt-6">
                                            <div className="flex items-center gap-2 text-gray-400 text-sm">
                                                <CloseCircleOutlined />
                                                Call Ended at {moment(data.end_time).format("HH:mm:ss")}
                                            </div>
                                        </div>
                                    </>
                                ) : analysis?.transcript && analysis.transcript.length > 0 ? (
                                    // @@ AI-COMMENT: Fallback to analysis.transcript if events array is missing
                                    <div className="space-y-3">
                                        {analysis.transcript.map((entry: any, index: number) => {
                                            const isAgent = entry.speaker === 'speaker1';
                                            return (
                                                <div key={index} className={`flex flex-col ${isAgent ? 'items-start' : 'items-end'}`}>
                                                    <div className="flex items-center gap-2 mb-1">
                                                        {isAgent && <RobotOutlined className="text-purple-500" />}
                                                        <span className="text-xs font-semibold text-gray-500">
                                                            {isAgent ? 'Sahara' : 'User'}
                                                        </span>
                                                        {!isAgent && <UserOutlined className="text-blue-500" />}
                                                    </div>
                                                    <div className={`rounded-lg p-3 max-w-[80%] shadow-sm text-sm ${isAgent
                                                        ? 'bg-gray-50 border border-gray-100 rounded-tl-none text-gray-800'
                                                        : 'bg-blue-50 border border-blue-100 rounded-tr-none text-gray-800'
                                                        }`}>
                                                        {entry.text}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <div className="text-center text-gray-400 py-10">No conversation data available</div>
                                )}
                            </Card>
                        </Col>
                    </Row>
                </div>
            ) : (
                <div className="flex justify-center items-center h-full text-gray-400">
                    No details available for this call.
                </div>
            )}
        </Modal>
    );
};

export default SaharaCallDetails;
